import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const initialState = {
  familyFeedbacks: [],
  currentFamilyFeedback: null,
  isLoading: false,
  error: null,
};

export const fetchFamilyFeedbacks = createAsyncThunk(
  'familyFeedback/fetchAll',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/family-feedbacks/${patientId}` : '/family-feedbacks';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch Family Feedback');
    }
  }
);

export const fetchFamilyFeedback = createAsyncThunk(
  'familyFeedback/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/family-feedbacks/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch Family Feedback');
    }
  }
);

export const createFamilyFeedback = createAsyncThunk(
  'familyFeedback/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/family-feedbacks', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to create Family Feedback');
    }
  }
);

export const updateFamilyFeedback = createAsyncThunk(
  'familyFeedback/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/family-feedbacks/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to update Family Feedback');
    }
  }
);

export const deleteFamilyFeedback = createAsyncThunk(
  'familyFeedback/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/family-feedbacks/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete Family Feedback');
    }
  }
);

const slice = createSlice({
  name: 'familyFeedback',
  initialState,
  reducers: {
    clearCurrentFamilyFeedback: (state) => { state.currentFamilyFeedback = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFamilyFeedbacks.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchFamilyFeedbacks.fulfilled, (s, a) => { s.isLoading = false; s.familyFeedbacks = a.payload; })
      .addCase(fetchFamilyFeedbacks.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(fetchFamilyFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchFamilyFeedback.fulfilled, (s, a) => { s.isLoading = false; s.currentFamilyFeedback = a.payload; })
      .addCase(fetchFamilyFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(createFamilyFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(createFamilyFeedback.fulfilled, (s, a) => { s.isLoading = false; s.familyFeedbacks.unshift(a.payload); })
      .addCase(createFamilyFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(updateFamilyFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(updateFamilyFeedback.fulfilled, (s, a) => {
        s.isLoading = false;
        s.currentFamilyFeedback = a.payload;
        const i = s.familyFeedbacks.findIndex((r) => r.id === a.payload.id);
        if (i !== -1) s.familyFeedbacks[i] = a.payload;
      })
      .addCase(updateFamilyFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(deleteFamilyFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(deleteFamilyFeedback.fulfilled, (s, a) => { s.isLoading = false; s.familyFeedbacks = s.familyFeedbacks.filter((r) => r.id !== a.payload.id); })
      .addCase(deleteFamilyFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; });
  },
});

export const { clearCurrentFamilyFeedback, clearError } = slice.actions;
export default slice.reducer;
